import { AnimatePresence } from 'framer-motion'
import { useEffect } from 'react'
import { Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom'
import './App.css'
import PageTransition from './components/PageTransition'
import { allImages } from './configs/allImages'
import { allSounds } from './configs/allSounds'
import { LanguageProvider } from './context/LanguageContext'
import About from './pages/About'
import { Accomplishments } from './pages/accomplishments/Accomplishments'
import { Battlepass } from './pages/battlepass/Battlepass'
import { ConsumableShop } from './pages/consumableShop/ConsumableShop'
import { DonateShop } from './pages/donate/DonateShop'
import { EventHud } from './pages/driftEvents/hud/EventHud'
import { FineTuning } from './pages/fineTuning/FineTuning'
import { Freeroam } from './pages/freeroam/Freeroam'
import { HallOfFame } from './pages/hallOfFame/HallOfFame'
import Home from './pages/Home'
import { GameHud } from './pages/hud/GameHud'
import { Speedometer } from './pages/hud/speedometer/Speedometer'
import Login from './pages/login/Login'
import MainPage from './pages/main/MainPage'
import { PasswordRecovery } from './pages/passwordRecovery/PasswordRecovery'
import MedalTilt from './pages/preloader/Medaile'
import { Preloader } from './pages/preloader/Preloader'
import { PreloadOverlay } from './pages/preloader/PreloadOverlay'
import { Preview } from './pages/preview/Preview'
import { Countdown } from './pages/race/countdown/Countdown'
import { Finish } from './pages/race/finish/Finish'
import { Frames } from './pages/race/finish/frames/Frames'
import { Preparation } from './pages/race/preparation/Preparation'
import Races from './pages/Races'
import Register from './pages/register/Register'
import { Research } from './pages/research/Research'
import { ModulesResearch } from './pages/researchModules/ModulesResearch'
import { TermsViewer } from './pages/termsViewer/TermsViewer'
import { TestDriveHud } from './pages/test-drive/TestDriveHud'
import { Tuning } from './pages/tuning/Tuning'
import { Vinyls } from './pages/vinyls/Vinyls'
import { ClientApi } from './services/clientApi'
import { useStore } from './store/store'
import { NuiMessages } from './utils/ClientEvents'
import { Config } from './utils/config'

const VIEW_ROUTES: Record<string, string> = {
  home: '/',
  login: '/login',
  register: '/register',
  main: '/main',
  races: '/races',
  about: '/about',
  hud: '/hud',
  freeroam: '/freeroam',
  menu: '/menu',
  event: '/event',
  preload: '/preload',
  research: '/research',
  modulesResearch: '/modules-research',
  donate: '/donate',
  battlepass: '/battlepass',
  achievements: '/achievements',
  vinyls: '/vinyls',
  tuning: '/tuning',
  fineTuning: '/fine-tuning',
  hallOfFame: '/halloffame',
  consumableShop: '/consumable-shop',
  passwordRecovery: '/passwordRecovery',
  terms: '/terms',
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppContent />
        {/* <DebugPanel /> */}
      </Router>
    </LanguageProvider>
  )
}

const AppContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isInitialized, checkAuth } = useStore()

  Object.defineProperty(window, 'hide', {
    value: () => {
      localStorage.setItem("shouldHidePreloader", "true")
      window.postMessage({ type: NuiMessages.PRELOADER_HIDE }, '*')
    },
    writable: true,
    enumerable: false,
    configurable: true,
  })

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  useEffect(() => {
    ClientApi.on(NuiMessages.SET_VIEW, ({ view, data }: { view: string, data?: Record<string, any> }) => {

      const route = VIEW_ROUTES[view]
      console.log(`Routing to ${view} ${route} ${JSON.stringify(data)}`)
      if (route) {
        if (data?.playerId) {
          navigate(`${route}?playerId=${data.playerId}`)
        } else if (data?.activityId) {
          navigate(`${route}?activityId=${data.activityId}`)
        } else if (data?.finishId) {
          navigate(`${route}?finishId=${data.finishId}`)
        } else if (route === "/hud" && data?.hudType) {
          navigate(`${route}?typeId=${data.hudType}`)
        } else {
          navigate(route)
        }
      }
    })
  }, [navigate])

  const preloadImages = () => {
    allImages.forEach((src) => {
      const img = new Image()
      img.src = src
    })
  }

  const preloadSounds = () => {
    allSounds.forEach((src) => {
      const audio = new Audio()
      audio.src = src
    })
  }

  useEffect(() => {
    preloadImages()
    preloadSounds()
  }, [])

  // Show loading only during initial authentication check
  if (!isInitialized) {
    return (
      <>
        <PreloadOverlay />
        <div className="loading">Loading...</div>
      </>
    )
  }

  return (
    <div style={{ background: Config.MAIN_BODY_BACKGROUND }}>
      <PreloadOverlay />
      <Speedometer />
      <Countdown />
      <Preparation />
      <Frames />
      <Finish />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={
            <PageTransition>
              <Home />
            </PageTransition>
          } />
          <Route path="/login" element={
            <PageTransition>
              <Login />
            </PageTransition>
          } />
          <Route path="/tuning" element={
            <Tuning />
          } />
          <Route path="/register" element={
            <PageTransition>
              <Register />
            </PageTransition>
          } />
          <Route path="/races" element={
            <PageTransition>
              <Races />
            </PageTransition>
          } />
          <Route path="/about" element={
            <PageTransition>
              <About />
            </PageTransition>
          } />
          <Route path="/main" element={
            <PageTransition>
              <MainPage />
            </PageTransition>
          } />
          <Route path="/halloffame" element={
            <PageTransition>
              <HallOfFame />
            </PageTransition>
          } />
          <Route path="/preload" element={
            <Preloader />
          } />
          <Route path="/research" element={
            <Research />
          } />
          <Route path="/modules-research" element={
            <ModulesResearch />
          } />
          <Route path="/donate" element={
            <DonateShop />
          } />
          <Route path="/battlepass" element={
            <Battlepass />
          } />
          <Route path="/achievements" element={
            <Accomplishments />
          } />
          <Route path="/hud" element={
            <GameHud />
          } />
          <Route path="/terms" element={
            <TermsViewer />
          } />
          <Route path="/passwordRecovery" element={
            <PasswordRecovery />
          } />
          <Route path="/vinyls" element={
            <Vinyls />
          } />
          <Route path="/freeroam" element={
            <Freeroam />
          } />
          <Route path="/consumable-shop" element={
            <ConsumableShop />
          } />
          <Route path="/medal" element={
            <MedalTilt src="/ranks/10.webp" />
          } />
          <Route path="/event" element={
            <EventHud />
          } />
          <Route path="/fine-tuning" element={
            <FineTuning />
          } />
          <Route path="/preview" element={
            <Preview />
          } />
          <Route path="/test-drive" element={
            <TestDriveHud />
          } />
        </Routes>
      </AnimatePresence>
    </div>
  )
}

export default App
